/** AML compliance severity → UI color tokens (.cursorrules §6). */

const DEFAULT_STYLE = {
  card: 'bg-emerald-50/50 border-emerald-100',
  icon: 'bg-emerald-100 text-emerald-600',
  label: 'text-emerald-600',
  risk: 'text-emerald-700',
  badge: 'bg-emerald-100 text-emerald-800',
};

const STYLES = {
  Wanted: {
    card: 'bg-purple-50 border-purple-300',
    icon: 'bg-purple-100 text-purple-700',
    label: 'text-purple-800',
    risk: 'text-purple-800',
    badge: 'bg-purple-100 text-purple-900',
  },
  Sanctioned: {
    card: 'bg-red-50 border-red-200',
    icon: 'bg-red-100 text-red-600',
    label: 'text-red-700',
    risk: 'text-red-700',
    badge: 'bg-red-100 text-red-800',
  },
  PEP: {
    card: 'bg-amber-50 border-amber-200',
    icon: 'bg-amber-100 text-amber-700',
    label: 'text-amber-800',
    risk: 'text-amber-800',
    badge: 'bg-amber-100 text-amber-900',
  },
  CRITICAL: {
    card: 'bg-red-50 border-red-200',
    icon: 'bg-red-100 text-red-600',
    label: 'text-red-700',
    risk: 'text-red-700',
    badge: 'bg-red-100 text-red-800',
  },
  CLEAN: DEFAULT_STYLE,
};

const FAILED_STYLE = {
  card: 'bg-slate-50 border-slate-200',
  icon: 'bg-slate-200 text-slate-500',
  label: 'text-slate-500',
  risk: 'text-slate-500',
  badge: 'bg-slate-100 text-slate-600',
};

const DARK_DEFAULT_STYLE = {
  shell: 'border-emerald-500/30',
  card: 'border-emerald-500/25 bg-emerald-950/20',
  icon: 'border border-emerald-500/30 bg-emerald-950/50 text-emerald-300',
  label: 'text-emerald-300',
  risk: 'text-emerald-200',
  badge: 'border border-emerald-500/30 bg-emerald-950/60 text-emerald-200',
  nested: 'border-white/10 bg-slate-900/70',
  body: 'text-slate-100',
  muted: 'text-slate-400',
};

const DARK_STYLES = {
  Wanted: {
    shell: 'border-purple-500/35',
    card: 'border-purple-500/30 bg-purple-950/25',
    icon: 'border border-purple-500/35 bg-purple-950/50 text-purple-300',
    label: 'text-purple-300',
    risk: 'text-purple-200',
    badge: 'border border-purple-500/35 bg-purple-950/60 text-purple-200',
    nested: 'border-purple-500/20 bg-slate-900/70',
    body: 'text-slate-100',
    muted: 'text-slate-400',
  },
  Sanctioned: {
    shell: 'border-red-500/40',
    card: 'border-red-500/35 bg-red-950/30',
    icon: 'border border-red-500/35 bg-red-950/50 text-red-300',
    label: 'text-red-300',
    risk: 'text-red-200',
    badge: 'border border-red-500/35 bg-red-950/60 text-red-200',
    nested: 'border-red-500/20 bg-slate-900/70',
    body: 'text-slate-100',
    muted: 'text-slate-400',
  },
  PEP: {
    shell: 'border-amber-500/35',
    card: 'border-amber-500/30 bg-amber-950/20',
    icon: 'border border-amber-500/35 bg-amber-950/50 text-amber-300',
    label: 'text-amber-300',
    risk: 'text-amber-200',
    badge: 'border border-amber-500/35 bg-amber-950/60 text-amber-200',
    nested: 'border-amber-500/20 bg-slate-900/70',
    body: 'text-slate-100',
    muted: 'text-slate-400',
  },
  CRITICAL: {
    shell: 'border-red-500/40',
    card: 'border-red-500/35 bg-red-950/30',
    icon: 'border border-red-500/35 bg-red-950/50 text-red-300',
    label: 'text-red-300',
    risk: 'text-red-200',
    badge: 'border border-red-500/35 bg-red-950/60 text-red-200',
    nested: 'border-red-500/20 bg-slate-900/70',
    body: 'text-slate-100',
    muted: 'text-slate-400',
  },
  CLEAN: DARK_DEFAULT_STYLE,
};

const DARK_FAILED_STYLE = {
  shell: 'border-slate-500/30',
  card: 'border-slate-500/25 bg-slate-900/40',
  icon: 'border border-slate-500/30 bg-slate-900/80 text-slate-300',
  label: 'text-slate-300',
  risk: 'text-slate-400',
  badge: 'border border-slate-500/30 bg-slate-900/60 text-slate-300',
  nested: 'border-white/10 bg-slate-900/70',
  body: 'text-slate-100',
  muted: 'text-slate-400',
};

export function normalizeComplianceSeverity(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'CLEAN';
  const upper = raw.toUpperCase();
  if (upper === 'CRITICAL') return 'Sanctioned';
  if (upper === 'PEP') return 'PEP';
  if (upper === 'SANCTIONED') return 'Sanctioned';
  if (upper === 'WANTED') return 'Wanted';
  if (upper === 'CLEAN') return 'CLEAN';
  return raw;
}

export function complianceSeverityStyle(severity, { failed = false, dark = false } = {}) {
  if (failed) return dark ? DARK_FAILED_STYLE : FAILED_STYLE;
  const key = normalizeComplianceSeverity(severity);
  if (dark) return DARK_STYLES[key] || DARK_DEFAULT_STYLE;
  return STYLES[key] || DEFAULT_STYLE;
}

export function isComplianceHit(res) {
  if (!res || res.ui_bucket !== 'compliance_alert') return false;
  const severity = normalizeComplianceSeverity(res.risk_level);
  if (severity !== 'CLEAN') return true;
  return Array.isArray(res.alerts) && res.alerts.length > 0;
}

export function complianceSeverityLabel(severity) {
  const key = normalizeComplianceSeverity(severity);
  if (key === 'CLEAN') return 'Clean';
  return key;
}
